import { observable, makeObservable, action, runInAction } from 'mobx';
import { api } from '../../constants/apis';
import { categoriesForBasic } from '../../constants/general';
import { deleteRequest, get, post, put } from '../../utils/axios-requests';

export default class EditCardUiStore {
  @observable error: string = '';
  @observable context: any = null;
  @observable loading: boolean = false;
  @observable card: any = null;
  @observable query: string = '';
  @observable userLanguages: Array<any> = [];
  @observable categories: Array<any> = [];
  @observable categoryInformation: any = {};
  @observable selectedInformation: Array<string> = [];
  @observable defaults: any = {};
  @observable images: Array<any> = [];

  constructor() {
    makeObservable(this);
  }

  @action
  public initliazeContext = (context: any) => {
    this.context = context;
  };

  @action
  public setError = (err: string) => {
    this.error = err;
  };

  public fetchCard = async (cardId: string) => {
    try {
      const res = await get(api.cards.info, {
        cardId,
      });

      runInAction(() => {
        this.card = res.data.card;
        this.selectedInformation = res.data.selectedIds;
        this.defaults = res.data.defaults;
      });

      return res.data.card;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchCategories = async () => {
    try {
      const res = await get(api.information.user, {
        userId: this.context.authenticatedUser.id,
      });

      runInAction(() => {
        this.categories = this.card.isBasic
          ? res.data.userCategories.filter((i: any) => categoriesForBasic.includes(i.name))
          : res.data.userCategories;
        this.userLanguages = res.data.infoLanguages;
      });

      return [res.data.infoLanguages, res.data.addedData];
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchCategoryInfo = async (category: string, fetch = false) => {
    try {
      if (this.categoryInformation[category] && !fetch) {
        return this.categoryInformation[category];
      }

      const res = await get(api.information.categoryInformation, {
        userId: this.context.authenticatedUser.id,
        categoryName: category,
      });

      this.categoryInformation[category] = res.data;
      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public resetCard = async () => {
    this.card = null;
  };

  @action
  public updateCardSettings = async (cardId: string, body: any) => {
    try {
      await put(api.cards.privacy, body, { cardId });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public getAvailability = async (id: string) => {
    try {
      const res = await get(api.availability.single, { id });
      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchInfoSuggestions = async (
    infoType: string,
    countryId: any = null,
    stateId: any = null
  ) => {
    try {
      const res = await get(api.informationList.single, {
        infoType,
        countryId,
        stateId,
      });

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public selectInfo = async (infoIds: Array<string>, cardId: string) => {
    try {
      await post(
        api.cards.info,
        { infoIds },
        {
          cardId,
        }
      );

      this.fetchCard(cardId);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public unselectInfo = async (infoIds: Array<string>, cardId: string) => {
    try {
      await deleteRequest(
        api.cards.info,
        {
          cardId,
        },
        { infoIds }
      );

      this.fetchCard(cardId);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public toggleDefault = async (
    infoId: string,
    label: string,
    category: string,
    cardId: string,
    sectionName: string = ''
  ) => {
    try {
      await post(
        api.cards.default,
        { infoId, label, category, sectionName },
        {
          cardId,
        }
      );

      //this.fetchCard(cardId);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public updateDefaultSection = async (sectionID: string, key: string) => {
    if (!this.defaults[key])
      this.defaults[key] = { sectionID: null, infoIDs: [] };
    this.defaults[key].sectionID = sectionID;
  };

  @action fetchImages = async () => {
    try {
      const res = await get(api.images.user, {
        userId: this.context.authenticatedUser.id,
      });

      runInAction(() => {
        this.images = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action uploadImage = async (url: string, public_id: string) => {
    try {
      await post(
        api.images.user,
        { url, public_id },
        {
          userId: this.context.authenticatedUser.id,
        }
      );

      this.fetchImages();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }
      return false;
    }
  };

  @action checkImages = async () => {
    try {
      const res = await get(api.images.checkImages, {
        userId: this.context.authenticatedUser.id,
      });

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action selectImg = async (imageUrl: string, cardId: string) => {
    try {
      await put(
        api.cards.image,
        { imageUrl },
        {
          cardId,
        }
      );

      this.fetchCard(cardId);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }
      return false;
    }
  };

  @action
  public deleteCard = async (cardId: string) => {
    try {
      await deleteRequest(api.cards.card, {
        cardId,
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action updateCardName = async (name: string, cardId: string) => {
    try {
      await put(
        api.cards.name,
        { name },
        {
          cardId,
        }
      );

      this.fetchCard(cardId);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }
      return false;
    }
  };
}
